import { useEffect, useState } from "react";
import api from "../lib/api";
import { Progress } from "./ui/progress";
import { formatMoney } from "../lib/usageFormat";

// Prompt 23 B4 — the company's own usage (Settings → Usage).
function Stat({ label, value, sub, testid }) {
  return (
    <div className="border border-neutral-200 p-5">
      <div className="label-mini">{label}</div>
      <div className="font-display text-3xl font-semibold mt-2" data-testid={testid}>{value}</div>
      {sub ? <div className="text-xs text-neutral-500 mt-1">{sub}</div> : null}
    </div>
  );
}

export default function CompanyUsage() {
  const [u, setU] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/usage/me").then(({ data }) => setU(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load usage"));
  }, []);

  if (err) return <div className="card-flat p-6 text-sm text-red-700" data-testid="usage-error">{err}</div>;
  if (!u) return <div className="card-flat p-6 text-sm text-neutral-500">Loading usage…</div>;
  const ft = u.free_tier;
  const active = u.daily.filter((d) => d.total > 0);

  return (
    <div className="space-y-6" data-testid="company-usage">
      <section className="card-flat p-6" data-testid="usage-free-tier">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-mini">free tier</div>
            <h2 className="font-display text-xl font-semibold mt-1">
              {ft.active ? "You're on the free tier"
                : ft.ended_by === "invoices" ? "Free tier finished: 100 invoices used"
                  : "Free tier finished: 6 months passed"}
            </h2>
            <p className="text-sm text-neutral-600 mt-1">
              Free for 100 invoices or 6 months from signup, whichever comes first. After that,
              {` ${formatMoney(u.price_per_invoice_usd)}`} per invoice delivered into your books.
            </p>
          </div>
          <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 font-mono ${
                  ft.active ? "border-emerald-600 text-emerald-700" : "border-neutral-900"}`}
                data-testid="usage-free-tier-status">
            {ft.active ? "active" : "ended"}
          </span>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-sm">
            <span data-testid="usage-free-used">{ft.used} of {ft.limit} invoices used</span>
            <span data-testid="usage-days-left">{ft.days_left} days left of {ft.months} months</span>
          </div>
          <Progress value={(ft.used / ft.limit) * 100} className="mt-2" />
          <p className="text-xs text-neutral-500 mt-2" data-testid="usage-free-tier-note">
            {ft.active
              ? `Ends when you reach ${ft.limit} invoices or in ${ft.days_left} days, whichever comes first.`
              : "Every invoice delivered from now on is billable."}
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="delivered this month" value={u.this_month.total} testid="usage-month-total" />
        <Stat label="billable this month" value={u.this_month.billable}
              sub={`${formatMoney(u.this_month.amount_usd)} so far`} testid="usage-month-billable" />
        <Stat label="delivered all time" value={u.all_time_total} testid="usage-all-time" />
      </div>

      <section className="card-flat p-6" data-testid="usage-daily">
        <div className="label-mini">last 30 days</div>
        <h2 className="font-display text-xl font-semibold mt-1">Invoices delivered per day</h2>
        {active.length === 0 ? (
          <p className="text-sm text-neutral-500 mt-4" data-testid="usage-daily-empty">
            No invoices delivered into your books in the last 30 days.
          </p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead><tr className="text-left label-mini">
              <th className="py-2">date</th><th>delivered</th><th>free tier</th><th>billable</th>
            </tr></thead>
            <tbody>
              {active.map((d) => (
                <tr key={d.date} className="border-t border-neutral-200" data-testid={`usage-day-${d.date}`}>
                  <td className="py-2 font-mono">{d.date}</td><td>{d.total}</td>
                  <td>{d.free_tier}</td><td>{d.billable}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
